import React, { useState, useEffect, useRef } from "react";
import { PhoneCall, ChevronDown, Check, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const CallTestPage = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ name: "United States", code: "+1", flag: "🇺🇸" });
  const dropdownRef = useRef(null);
  const countriesListRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    fetch("https://restcountries.com/v3.1/all")
      .then((res) => res.json())
      .then((data) => {
        const formattedData = data
          .map((country) => ({
            name: country.name.common,
            code: country.idd?.root + (country.idd?.suffixes?.[0] || ""),
            flag: country.flag,
          }))
          .filter((country) => country.code);
        setCountries(formattedData);
        setFilteredCountries(formattedData);
      })
      .catch((error) => console.error("Error fetching countries:", error));
  }, []);

  // Update filtered countries whenever search changes
  useEffect(() => {
    setFilteredCountries(getFilteredAndSortedCountries());
  }, [search, countries]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  // Prevent page scrolling
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleCall = async () => {
    if (!phoneNumber) {
      toast({
        title: "Error",
        description: "Please enter a phone number.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("http://mci.lclhst.com/make-outbound-call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: countryCode + phoneNumber })
      });
      const data = await response.json();
      if (response.ok && data.callSid) {
        toast({
          title: "Success",
          description: "Call initiated successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to initiate call.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error triggering call:", error);
      toast({
        title: "Error",
        description: "Network error.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectCountry = (country) => {
    setSelectedCountry(country);
    setCountryCode(country.code);
    setIsDropdownOpen(false);
    setSearch("");
  };

  // Advanced search function with dynamic sorting by relevance
  const getFilteredAndSortedCountries = () => {
    const searchLower = search.toLowerCase().trim();
    
    if (!searchLower) {
      return countries; // Return all countries if no search
    }
    
    return countries
      .filter((country) => {
        const nameLower = country.name.toLowerCase();
        return (
          nameLower.includes(searchLower) || 
          country.code.includes(searchLower)
        );
      })
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        
        // Exact matches come first
        if (aName === searchLower && bName !== searchLower) return -1;
        if (bName === searchLower && aName !== searchLower) return 1;
        
        // Then starts with matches
        const aStartsWith = aName.startsWith(searchLower);
        const bStartsWith = bName.startsWith(searchLower);
        if (aStartsWith && !bStartsWith) return -1;
        if (bStartsWith && !aStartsWith) return 1;
        
        // Then check for word matches (e.g. searching "new" should prioritize "New Zealand")
        const aWordMatch = aName.split(" ").some(word => word.toLowerCase().startsWith(searchLower));
        const bWordMatch = bName.split(" ").some(word => word.toLowerCase().startsWith(searchLower));
        if (aWordMatch && !bWordMatch) return -1;
        if (bWordMatch && !aWordMatch) return 1;
        
        // Then contains matches, ordered by position of match
        const aPos = aName.indexOf(searchLower);
        const bPos = bName.indexOf(searchLower);
        if (aPos !== -1 && bPos !== -1) return aPos - bPos;
        
        // Default to alphabetical
        return aName.localeCompare(bName);
      });
  };

  // Prevent default scroll behavior on dropdown
  const handleDropdownScroll = (e) => {
    e.stopPropagation();
  };

  // Handle search input change - update immediately
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center py-12 px-4">
      <div className="flex flex-col items-center text-center mb-5 w-full">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Request call back</h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl px-6">
          AI-Powered Call Agents for Efficient Hospital Intake and Recovery Support
        </p>
      </div>

      <div className="w-full max-w-md p-8 bg-white rounded-xl shadow-lg">
        <div className="space-y-6">
          <div className="flex space-x-4">
            <div className="w-2/5 relative" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <button
                type="button"
                className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg text-lg bg-white"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className="flex items-center">
                  <span className="mr-2 text-xl">{selectedCountry.flag}</span>
                  <span className="truncate">{selectedCountry.code}</span>
                </div>
                <ChevronDown size={18} />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                  <div className="p-2 sticky top-0 bg-white z-20 border-b">
                    <input 
                      type="text" 
                      placeholder="Search countries..."
                      className="w-full p-2 border border-gray-300 rounded"
                      value={search}
                      onChange={handleSearchChange}
                      autoFocus
                    />
                  </div>
                  <div 
                    className="max-h-60 overflow-y-auto"
                    onWheel={handleDropdownScroll}
                    ref={countriesListRef}
                  >
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => (
                        <div
                          key={country.code}
                          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => selectCountry(country)}
                        >
                          <span className="mr-2">{country.flag}</span>
                          <span className="truncate">{country.name}</span>
                          <span className="ml-auto text-gray-600">{country.code}</span>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-center text-gray-500">No matching countries found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="w-3/5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                placeholder="Enter Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>
          
          <button
            onClick={handleCall}
            disabled={isSubmitting}
            className="w-full p-2 bg-purple-500 text-white rounded-lg text-base font-medium hover:bg-purple-600 transition-colors flex items-center justify-center"
          >
            {isSubmitting ? (
              <span className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              <span className="inline-flex items-center">
                <PhoneCall size={16} className="mr-2" /> Call Back
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallTestPage;